/* eslint-disable react/prop-types */
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@fs/zion-flags'
import { H6, IconButton, Row, useStatusOverlay, LabelTooltip, colors, MenuOverlay, ListItem } from '@fs/zion-ui'
import {
  ContentCopy,
  ContentDownload,
  ContentEdit,
  MenuMore,
  NoticeInfo,
  ControlHide,
  ControlView,
} from '@fs/zion-icon'
import { copyToClipboard } from '@fs/zion-frontend-friends'
import useNamesExperienceOptIn from '../../../../hooks/useNamesExperienceOptIn'
import { useFullTextStateContext } from '../../providers/FullTextContext'

const FullTextToolbar = ({
  plainTextTranscript,
  downloadOverlay,
  handleEditModeToggle,
  showKeywords,
  setShowKeywords,
}) => {
  const [t] = useTranslation()
  const showStatusOverlay = useStatusOverlay()
  const fullTextState = useFullTextStateContext()
  const downloadPermission = fullTextState?.downloadPermission ?? false
  const { isOptedIn } = useNamesExperienceOptIn()
  const { rip_sharedImageViewer_showTranscriptEditButton: showTranscriptEditButton } = useFeatureFlags([
    'rip_sharedImageViewer_showTranscriptEditButton',
  ])

  const handleCopyTranscript = () => {
    copyToClipboard(
      plainTextTranscript,
      'text/plain',
      () => {
        showStatusOverlay({
          message: t('full-text.copy-success', 'Full transcript copied to clipboard'),
        })
      },
      () => {
        showStatusOverlay({
          message: t('full-text.copy-error', 'Error copying full transcript'),
          type: 'error',
        })
      }
    )
  }

  return (
    <Row alignY="middle">
      {!isOptedIn && <H6 growX>{t('full-text.transcript-heading', 'Transcript')}</H6>}
      {isOptedIn && (
        <Row growX gutters="nano">
          <H6>{t('full-text.transcript-heading-flow', 'Transcript')}</H6>
          <LabelTooltip
            target={<NoticeInfo alignWithText size="xs" color={colors.blue60} />}
            label={t('full-text.transcript-info', 'This tool is powered by AI. Responses may be incorrect.')}
          />
        </Row>
      )}

      {!isOptedIn && (
        <Row gutters="xs" noShrinkX>
          <IconButton
            data-testid="full-text-download"
            Icon={ContentDownload}
            linkName="full-text-download-start"
            title={
              downloadPermission
                ? t('full-text.download', 'Download Image and Transcription')
                : t('full-text.download.disabled', 'Due to contract restrictions this feature is disabled')
            }
            onClick={downloadPermission ? downloadOverlay.handleClick : undefined}
            disabled={!downloadPermission}
          />
          <IconButton
            data-testid="full-text-copy"
            Icon={ContentCopy}
            linkName="full-text-transcript-copy"
            title={t('full-text.copy-icon-text', 'Copy Transcription')}
            onClick={handleCopyTranscript}
          />
          {/* Temporarily hiding edit button https://icseng.atlassian.net/browse/RIP-4759 */}
          {showTranscriptEditButton.isOn && (
            <IconButton
              data-testid="full-text-edit"
              Icon={ContentEdit}
              linkName="full-text-edit"
              title={t('full-text.edit', 'Edit Transcription')}
              onClick={handleEditModeToggle}
            />
          )}
        </Row>
      )}
      {isOptedIn && (
        <Row gutters="xs" noShrinkX>
          <MenuOverlay
            menuButton={
              <IconButton
                data-testid="full-text-transcript-menu"
                linkName="full-text-transcript-menu"
                Icon={MenuMore}
                title={t('full-text-transcript.menu', 'Transcript options menu')}
              />
            }
            aria-label={t('full-text-transcript.menu', 'Transcript options menu')}
          >
            {/* Temporarily hiding edit button https://icseng.atlassian.net/browse/RIP-4759 */}
            {showTranscriptEditButton.isOn && (
              <ListItem
                onClick={handleEditModeToggle}
                primaryText={t('full-text-transcript.edit', 'Edit Transcript')}
                linkName="full-text-edit"
                startElement={<ListItem.Icon Icon={ContentEdit} />}
                data-testid="full-text-edit"
              />
            )}
            <ListItem
              onClick={handleCopyTranscript}
              primaryText={t('full-text-transcript.copy', 'Copy Transcript')}
              linkName="full-text-copy"
              startElement={<ListItem.Icon Icon={ContentCopy} />}
              data-testid="full-text-copy"
            />
            <ListItem
              onClick={downloadPermission ? downloadOverlay.handleClick : undefined}
              primaryText={t('full-text-transcript.download', 'Download Transcript')}
              secondaryText={
                !downloadPermission
                  ? t('full-text.download.disabled', 'Due to contract restrictions this feature is disabled')
                  : undefined
              }
              startElement={<ListItem.Icon Icon={ContentDownload} />}
              linkName="full-text-download"
              data-testid="full-text-download"
              disabled={!downloadPermission}
            />
            <ListItem
              onClick={() => {
                setShowKeywords(!showKeywords)
              }}
              primaryText={
                showKeywords
                  ? t('full-text-transcript.hide-keywords', 'Hide Keywords')
                  : t('full-text-transcript.show-keywords', 'Show Keywords')
              }
              startElement={showKeywords ? <ListItem.Icon Icon={ControlHide} /> : <ListItem.Icon Icon={ControlView} />}
              data-testid={showKeywords ? 'full-text-hide-keywords' : 'full-text-show-keywords'}
              linkName={showKeywords ? 'full-text-hide-keywords' : 'full-text-show-keywords'}
            />
          </MenuOverlay>
        </Row>
      )}
    </Row>
  )
}

export default FullTextToolbar
